/**
 * Agent complexity heuristic — TS port of backend.ai.agent_complexity.
 *
 * Mirrors evaluate_agent_complexity exactly. Both implementations are pinned
 * to the same outputs by frontend/src/utils/__tests__/agentComplexity.contract.test.ts.
 *
 * Signals (1 point each):
 *   - long_instructions     : len(instructions) >= 1500
 *   - has_tools             : len(tools) >= 1
 *   - multiple_handoffs     : len(handoff_agents) >= 2
 *   - structured_output     : output_schema != null OR output_model_id != null (counted once)
 *   - has_output_guardrails : len(output_guardrails) >= 1
 *
 * isComplex when score >= 3.
 *
 * Note: TS interface uses `isComplex` (camelCase); backend exposes `is_complex`.
 */

export interface ComplexityInput {
  instructions?: string | null;
  tools?: any[] | null;
  handoff_agents?: any[] | null;
  output_schema?: any | null;
  output_model_id?: number | null;
  output_guardrails?: any[] | null;
}

export interface ComplexityResult {
  score: number;
  signals: string[];
  isComplex: boolean;
}

export function evaluateAgentComplexity(agent: ComplexityInput): ComplexityResult {
  const signals: string[] = [];

  const instructions = agent.instructions ?? '';
  if (instructions.length >= 1500) {
    signals.push('long_instructions');
  }

  const tools = agent.tools ?? [];
  if (tools.length >= 1) {
    signals.push('has_tools');
  }

  const handoffs = agent.handoff_agents ?? [];
  if (handoffs.length >= 2) {
    signals.push('multiple_handoffs');
  }

  if (agent.output_schema != null || agent.output_model_id != null) {
    signals.push('structured_output');
  }

  const guardrails = agent.output_guardrails ?? [];
  if (guardrails.length >= 1) {
    signals.push('has_output_guardrails');
  }

  const score = signals.length;
  return { score, signals, isComplex: score >= 3 };
}
